<template>
  <section class="plans-tiles">
    <h2 class="plans-tiles__heading">
      {{ data?.titles?.title }}
    </h2>
    <!-- Yearly headline reads `yearlyPrice` + `yearlyPriceSuffix`
         from plans.js. Free tile opts out. -->
    <div class="plans-tiles__period">
      <div
        class="plans-tiles__period-toggle"
        role="tablist"
        :aria-label="labels.toggleAriaLabel"
      >
        <button
          type="button"
          role="tab"
          class="plans-tiles__period-btn"
          :class="{ 'plans-tiles__period-btn--active': period === 'monthly' }"
          :aria-selected="period === 'monthly'"
          @click="period = 'monthly'"
        >{{ labels.monthly }}</button>
        <button
          type="button"
          role="tab"
          class="plans-tiles__period-btn"
          :class="{ 'plans-tiles__period-btn--active': period === 'yearly' }"
          :aria-selected="period === 'yearly'"
          @click="period = 'yearly'"
        >
          {{ labels.yearly }}
          <span
            class="plans-tiles__period-savings"
            aria-hidden="true"
          >
            <span class="plans-tiles__period-savings-sparkle">✦</span>
            {{ labels.savings }}
          </span>
        </button>
      </div>
    </div>
    <div class="plans-tiles__row">
      <div
        v-for="(item, index) in data?.feature_tiles"
        v-bind:key="index"
        class="plans-tile"
        :class="{
          'plans-tile--popular': item?.popular,
          'plans-tile--premium': item?.premium,
        }"
      >
        <!-- Pill badge on the recommended tier. Deliberately not
             labeled "Popular" / "Best seller" / "Most chosen". the
             dryly descriptive text gently mocks the social-proof
             trope every pricing page leans on ("Less than a
             coffee" / "Moins qu'une baguette de pain"). Sparkle
             stays. we're not above the visual flair, just the
             language. Text comes from data so it stays bilingual. -->
        <span
          v-if="item?.popular"
          class="plans-tile__pill"
          aria-label="Recommended"
        >
          <span class="plans-tile__pill-sparkle"
aria-hidden="true">✦</span>
          {{ displayedPopularLabel(item) }}
        </span>

        <!-- Same pill mechanic on the premium tier, in the gold
             register so it visually belongs to the Master tile's
             cream/gold theme. Different style modifier, same
             sparkle. Self-aware joke about the price ("Moins que
             99 baguettes de pain" / "Less than 99 coffees"). -->
        <span
          v-if="item?.premium && item?.premiumLabel"
          class="plans-tile__pill plans-tile__pill--premium"
          aria-label="Top tier"
        >
          <span class="plans-tile__pill-sparkle"
aria-hidden="true">✦</span>
          {{ item?.premiumLabel }}
        </span>

        <span
          v-if="item?.freeLabel"
          class="plans-tile__pill plans-tile__pill--free"
        >
          <span class="plans-tile__pill-sparkle"
aria-hidden="true">✦</span>
          {{ item?.freeLabel }}
        </span>

        <!-- Optional decorative icon. Skipped when null in data. -->
        <img
          v-if="item?.icon?.url"
          class="plans-tile__icon"
          :src="item?.icon?.url"
          :alt="item?.icon?.alternativeText || ''"
        />

        <h3 class="plans-tile__title">{{ item?.title }}</h3>

        <div class="plans-tile__price-block">
          <!-- Price is split into "whole" + "fraction" (e.g. "€0" +
               ".99") so the decimal part can render smaller and
               raised. Standard typographic move (Apple, Stripe).
               Keeps "€0.99" readable without giving the cents the
               same visual weight as the headline number. Free tile
               (priceSmall:true) has no decimal; whole renders the
               full label ("Forever"), fraction is empty. -->
          <span
            class="plans-tile__price"
            :class="{ 'plans-tile__price--small': item?.priceSmall }"
          >
            <span class="plans-tile__price-whole">{{ splitPrice(displayedPrice(item)).whole }}</span>
            <span
              v-if="splitPrice(displayedPrice(item)).frac"
              class="plans-tile__price-frac"
            >{{ splitPrice(displayedPrice(item)).frac }}</span>
          </span>
          <!-- Inline unit suffix ("/month" or "/year" depending on the
               toggle ; "/mois" or "/an" in FR). Sits right next to the
               price so "€1/month" reads as a single unit. Smaller +
               dimmed so the number is still the focal point. Free tile
               has no suffix in either period. -->
          <span
            v-if="displayedSuffix(item)"
            class="plans-tile__price-unit"
          >{{ displayedSuffix(item) }}</span>
        </div>

        <ul class="plans-tile__features">
          <li
            v-for="(category, indexCat) in relabeledCategories(item)"
            v-bind:key="indexCat"
          >
            <span class="plans-tile__feature-check"
aria-hidden="true">✓</span>
            {{ category?.label }}
          </li>
        </ul>

        <div class="plans-tile__cta-wrap">
          <!-- The CTA used to be a Link to /hello (stub) since the
               app isn't on the stores yet. Until store URLs and
               universal links exist, scroll the user down to the
               RotatingWords section instead. it already has both
               platform buttons (App Store / Play Store). Same
               behavior for all three tiers. swap to a real
               per-tier deep link once available. -->
          <button
            type="button"
            class="plans-tile__cta-btn"
            @click="scrollToDownload"
          >{{ item?.button?.label }}</button>
        </div>

        <!-- Sub-price line lives at the BOTTOM of the tile (not inline
             with the price). Keeps the price slot clean and visually
             quiet. it's a brand-voice aside, not a number. Replaces
             the old "hook" line. -->
        <p
          v-if="item?.subPrice"
          class="plans-tile__sub-price"
        >{{ item?.subPrice }}</p>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState } from 'pinia'
import { useStore } from '@/stores/index'

/* Localized label to swap in for the legacy "auto-check / veille"
   bullets that come from Strapi. We rename them client-side so the
   tile bullets stay consistent with the new "Heartbeat / Battement"
   row in the pricing TABLE just above. Once Strapi is updated to
   use this wording directly, both the relabel + the matcher below
   can be deleted. */
const HEARTBEAT_LABEL = {
  'en-US': 'Heartbeat',
  'fr-FR': 'Battement',
}

/* Yearly UX labels. `savings` is the badge text floating above the
   Yearly toggle segment. */
const PERIOD_LABELS = {
  'en-US': {
    monthly: 'Monthly',
    yearly: 'Yearly',
    savings: 'Best price',
    toggleAriaLabel: 'Billing period',
  },
  'fr-FR': {
    monthly: 'Mensuel',
    yearly: 'Annuel',
    savings: 'Meilleur prix',
    toggleAriaLabel: 'Période de facturation',
  },
}

/* Matches the legacy "auto-check / veille" wording in whatever
   form Strapi happens to store it ("auto-check", "Enhanced
   auto-check", "veille renforcée", etc.). Permissive substring
   match so we don't need to enumerate every spelling variant. */
const isAutoCheckLabel = (label) => {
  if (!label) return false
  const normalized = String(label).trim().toLowerCase()
  return (
    normalized.includes('auto-check') ||
    normalized.includes('auto check') ||
    normalized.includes('autocheck') ||
    normalized.includes('veille')
  )
}

export default {
  name: 'PlansTilesSection',
  props: {
    data: Object
  },
  data() {
    return {
      /** 'yearly' | 'monthly'. Defaults to monthly (matches the app
       *  Shop). Pure FE state, not persisted; refresh re-defaults. */
      period: 'monthly',
    }
  },
  computed: {
    ...mapState(useStore, ['language']),
    heartbeatLabel() {
      return HEARTBEAT_LABEL[this.language] || HEARTBEAT_LABEL['en-US']
    },
    labels() {
      return PERIOD_LABELS[this.language] || PERIOD_LABELS['en-US']
    },
  },
  methods: {
    /** Return a tile's feature bullets with the legacy "auto-check /
     *  veille" labels swapped to the localized "Heartbeat / Battement"
     *  wording. Free-tier tile has no matching bullet → returned
     *  unchanged. Super and Master each have one matching bullet →
     *  both end up showing "Heartbeat / Battement" in their place. */
    relabeledCategories(tile) {
      const cats = tile?.feature_categories || []
      return cats.map((cat) => {
        if (isAutoCheckLabel(cat?.label)) {
          return { ...cat, label: this.heartbeatLabel }
        }
        return cat
      })
    },
    /** All three tier CTAs scroll to the RotatingWords section
     *  (`#get-the-app`) where the App Store + Play Store buttons
     *  live. Stubbed today because the app isn't on the stores
     *  yet. once it is, swap this for a per-tier deep link /
     *  universal link that opens the in-app subscription flow on
     *  the right tier. */
    scrollToDownload() {
      const target = document.getElementById('get-the-app')
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    /** Popular-pill text. Swaps to the yearly variant when the toggle
     *  is on Yearly (e.g. "Cheaper than a coffee" → "...10 coffees").
     *  Falls back to the monthly label if no yearly variant exists. */
    displayedPopularLabel(item) {
      if (this.period === 'yearly' && item?.popularLabelYearly) {
        return item.popularLabelYearly
      }
      return item?.popularLabel
    },
    /** Headline. Monthly = strict price. Yearly = explicit yearly
     *  billed total. Free tier passes through ("Forever"). */
    displayedPrice(item) {
      const price = item?.price
      if (!price) return ''
      if (item?.priceSmall) return price
      if (this.period === 'monthly') return price
      return item?.yearlyPrice || price
    },
    displayedSuffix(item) {
      if (item?.priceSmall) return item?.priceSuffix || ''
      if (this.period === 'yearly') return item?.yearlyPriceSuffix || ''
      return item?.priceSuffix || ''
    },
    /** Split a price string into its whole + fractional parts so the
     *  template can render the cents smaller. Handles both en-US
     *  decimal ("€0.99") and fr-FR comma ("€0,99") notations.
     *
     *  Examples:
     *    "€0.99"   → { whole: "€0",      frac: ".99" }
     *    "€99,99"  → { whole: "€99",     frac: ",99" }
     *    "€99"     → { whole: "€99",     frac: "" }
     *    "Forever" → { whole: "Forever", frac: "" }
     *
     *  Anchored to the END of the string (`$`) so something like
     *  "1,000" wouldn't mis-split if a future tier crosses €1k
     *  (the thousands separator wouldn't be the last group). */
    splitPrice(price) {
      if (!price) return { whole: '', frac: '' }
      const match = price.match(/[.,]\d+$/)
      if (!match) return { whole: price, frac: '' }
      return {
        whole: price.slice(0, match.index),
        frac: match[0],
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.plans-tiles {
  padding: 48px 0 32px;
}

.plans-tiles__heading {
  text-align: center;
  margin: 0 0 36px;
}

/* Billing-period toggle wrapper. Centers the pill horizontally and
   gives the "2 months free" badge that floats above the Yearly
   segment some room to breathe. Larger top margin pulls the toggle
   away from the heading ; bottom margin is the inverse of the row's
   top padding so the rhythm under the tiles is clean. */
.plans-tiles__period {
  display: flex;
  justify-content: center;
  margin: 0 0 40px;
}

/* Two-segment pill, brand-pink outline. Each segment is a real
   <button> so keyboard + screen-reader users get proper interaction.
   Background switches via the --active modifier when picked ; only
   one segment can be active at a time, role=tablist enforces that
   semantically. */
.plans-tiles__period-toggle {
  display: inline-flex;
  align-items: stretch;
  padding: 4px;
  background: white;
  border: 1px solid var(--bzioup-color-pink);
  border-radius: 999px;
  box-shadow: var(--bzioup-shadow-soft);
  position: relative;
}

.plans-tiles__period-btn {
  position: relative;
  padding: 8px 22px;
  background: transparent;
  border: 0;
  border-radius: 999px;
  font: inherit;
  font-weight: 600;
  color: var(--bzioup-color-Y);
  cursor: pointer;
  transition: background-color 180ms ease, color 180ms ease;
}

.plans-tiles__period-btn:focus-visible {
  outline: 2px solid var(--bzioup-color-pink);
  outline-offset: 2px;
}

.plans-tiles__period-btn--active {
  background: var(--bzioup-color-pink);
  color: white;
}

/* Floating savings badge above the Yearly segment. Gold register so
   it visually belongs to the Master tile's accent without competing
   with the pink toggle pill itself ; reads as a discrete reward
   call-out, not a label of the segment. Pulled into negative top
   margin to overlap the toggle's top edge. */
.plans-tiles__period-savings {
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px 3px 8px;
  background: var(--bzioup-color-premium, #1a1a1a);
  color: white;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
}

.plans-tiles__period-savings-sparkle {
  font-size: 11px;
  line-height: 1;
  animation: plans-pill-sparkle 2s ease-in-out infinite;
}

/* Row of tiles. flex so they sit side-by-side on desktop, stacked
   on mobile. The popular tile sits visually higher (translate -6px)
   on desktop only. it gently asserts itself without being aggressive
   about it. */
.plans-tiles__row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: stretch;
  gap: 20px;
  padding: 12px;
}

.plans-tile {
  position: relative;
  flex: 1 1 280px;
  max-width: 320px;
  display: flex;
  flex-direction: column;
  /* Top padding bumped from 32px so the title clears the social-
     proof pill (which sits absolute at top: -12px and extends down
     into the tile). Free tile has no pill but stays at the same
     padding so all three tiles line up at the title row. */
  padding: 52px 24px 24px;
  background: var(--bzioup-bg-color-A);
  /* Base border = brand pink. Free tile inherits this directly
     (no theme class). Super (.plans-tile--popular) keeps the
     pink border via its own rule below; Master
     (.plans-tile--premium) overrides to charcoal. All three tiles
     stay outlined in a brand color, no grey-on-grey defaults. */
  border: 1px solid var(--bzioup-color-pink);
  border-radius: $border-radius;
  text-align: center;
  transition: transform 220ms cubic-bezier(0.2, 0.8, 0.3, 1),
              box-shadow 220ms ease-out;
}

.plans-tile:hover {
  transform: translateY(-4px);
  box-shadow: var(--bzioup-shadow-soft);
}

/* Popular tile = the Super tier in the data. Soft pink tint, pink
   border, slight upward shift on desktop so it feels chosen-by-default.
   Pill badge sits on the top edge, pink + cream contrast. */
.plans-tile--popular {
  background: var(--bzioup-color-pink-soft);
  border-color: var(--bzioup-color-pink);
  box-shadow: var(--bzioup-shadow-soft);
}

.plans-tile--popular:hover {
  transform: translateY(-8px);
}

/* Premium tile = Master in the data. Charcoal accents + near-white
   background. Quieter than the pink tile: the visual cue for "top
   tier" comes from RESTRAINT (Apple Pro / Stripe pattern), not from
   metallic flash. The price gets the charcoal accent so the eye
   still registers "this one is the high tier" without competing
   with the popular pink tile. */
.plans-tile--premium {
  background: var(--bzioup-color-premium-soft);
  border-color: var(--bzioup-color-premium);
  /* Neutral shadow on the premium tile. layered with the base
     shadow so depth is consistent across tiles. tint is keyed to
     the charcoal palette (cool grey, not warm gold). */
  box-shadow:
    0 4px 14px rgba(0, 0, 0, 0.10),
    var(--bzioup-shadow-soft);
}

.plans-tile--premium .plans-tile__price {
  color: var(--bzioup-color-premium);
}

/* Mirror of the premium price-color rule on the popular tile.
   €1 in pink so the price visually belongs to the Super tile's
   pink theme (border + tinted background + pill). */
.plans-tile--popular .plans-tile__price {
  color: var(--bzioup-color-pink);
}

.plans-tile--premium .plans-tile__feature-check {
  color: var(--bzioup-color-premium);
}

/* The Master CTA gets the charcoal treatment. Filled dark pill,
   white text. Visually the most "this is the top" element on the
   page without being shouty. Override the white default below. */
.plans-tile--premium .plans-tile__cta-btn {
  background: var(--bzioup-color-premium);
  color: white;
  border-color: var(--bzioup-color-premium);
}

.plans-tile--premium .plans-tile__cta-btn:focus-visible {
  outline-color: var(--bzioup-color-premium);
}

.plans-tile__pill {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 14px 4px 10px;
  background: var(--bzioup-color-pink);
  color: white;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(218, 138, 139, 0.4);
  display: inline-flex;
  align-items: center;
  gap: 4px;
  /* Pill itself has a slow, subtle "breathing" pulse on shadow.
     Lives at the edge of perception. you notice it without naming
     it. Same registry as the BETA badge in the app. */
  animation: plans-pill-breathe 3.2s ease-in-out infinite;
}

/* Premium-tier pill: same shape + sparkle as the popular pill,
   but in the charcoal register so it visually belongs to the
   Master tile's dark-on-light theme. Neutral cool-grey shadow,
   breathing animation re-keyed to the charcoal palette. */
.plans-tile__pill--premium {
  background: var(--bzioup-color-premium);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35);
  animation: plans-pill-breathe-premium 3.2s ease-in-out infinite;
}

@keyframes plans-pill-breathe-premium {
  0%, 100% { box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35); }
  50%      { box-shadow: 0 3px 12px rgba(0, 0, 0, 0.55); }
}

.plans-tile__pill--free {
  background: var(--bzioup-bg-color-A);
  color: var(--bzioup-color-pink);
  border: 1px solid var(--bzioup-color-pink);
  box-shadow: none;
  animation: none;
}

.plans-tile__pill-sparkle {
  display: inline-block;
  font-size: 13px;
  line-height: 1;
  transform-origin: center;
  /* Faster, more articulated motion on the sparkle. layered with
     the pill's breathing so the two rhythms cross. */
  animation: plans-pill-sparkle 2s ease-in-out infinite;
}

@keyframes plans-pill-breathe {
  0%, 100% { box-shadow: 0 2px 6px rgba(218, 138, 139, 0.4); }
  50%      { box-shadow: 0 3px 12px rgba(218, 138, 139, 0.65); }
}

@keyframes plans-pill-sparkle {
  0%, 100% { transform: rotate(0deg) scale(1); }
  50%      { transform: rotate(180deg) scale(1.2); }
}

.plans-tile__icon {
  max-width: 48px;
  margin: 0 auto 12px;
}

.plans-tile__title {
  margin: 0 0 4px;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 0.2px;
}

.plans-tile__price-block {
  margin: 16px 0 24px;
}

.plans-tile__price-block {
  /* Flex row so the price + the small unit suffix ("/month") read
     as one composed line ("€1/month"), with the unit
     baseline-aligned to the big price digits. */
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 4px;
}

.plans-tile__price {
  /* Wrapper for the whole + frac sub-spans. font-size lives HERE
     (not on the children) so the small-variant (--small) can
     shrink the whole price uniformly by overriding just this rule,
     and so the frac's `em`-based sizing tracks the headline number
     proportionally. */
  display: inline-flex;
  align-items: baseline;
  font-size: 44px;
  font-weight: 700;
  line-height: 1;
  color: var(--bzioup-color-Z);
}

.plans-tile__price-whole {
  /* Inherits font-size from .plans-tile__price (44px on paid tiles,
     22px on the --small variant for Free). */
}

/* Decimal cents, same color as the whole, sized down to ~45% and
   nudged up to sit at the top of the headline x-height. Reads as a
   footnote on the number, not as more digits competing for
   attention. The currency stays attached to .whole (e.g. "€0"), so
   only the ".99"/",99" part lives in here. */
.plans-tile__price-frac {
  margin-left: 2px;
  font-size: 0.45em;
  font-weight: 700;
  /* Lift the cents toward the top of the price's cap-height so the
     line reads as "headline number with small superscript cents"
     rather than two equal halves. */
  transform: translateY(-0.45em);
}

/* Unit suffix sitting RIGHT next to the price ("€1 /month").
   Smaller + dimmed so the number stays the focal point and the
   unit reads as metadata, not as its own line of text. */
.plans-tile__price-unit {
  font-size: 14px;
  font-weight: 500;
  color: var(--bzioup-color-Y);
  opacity: 0.75;
}

/* Free tier: the "price" slot is a word, not a number ("Forever" /
   "Pour toujours"). Dialed down so it doesn't visually compete
   with €1 / €99 on the other two tiles. Title still says "Free"
   right above, so this line is just the permanence cue. */
.plans-tile__price--small {
  font-size: 22px;
  font-weight: 600;
  letter-spacing: 0.3px;
  /* Pad top a touch so the smaller line sits centered in the
     space that the larger prices would occupy. keeps the three
     tiles visually aligned at the features row. */
  padding-top: 18px;
}

.plans-tile__features {
  list-style: none;
  margin: 0 0 28px;
  padding: 0;
  text-align: left;
  flex: 1;
}

.plans-tile__features li {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 0 0 10px;
  font-size: 14px;
  line-height: 1.4;
  color: var(--bzioup-color-Y);
}

.plans-tile__feature-check {
  flex-shrink: 0;
  color: var(--bzioup-color-pink);
  font-weight: 700;
  line-height: 1.4;
}

.plans-tile__cta-wrap {
  margin: 0 0 16px;
}

/* Full-width pill CTA. native <button> (not a Link) so it can
   smooth-scroll to the RotatingWords section instead of routing
   anywhere. Color follows the tile's tier theme:
   - default (Free tile)  → white pill with dark text + soft border.
                            reads as the neutral option, not the
                            push.
   - .plans-tile--popular → pink (Super tile override below)
   - .plans-tile--premium → gold (Master tile override above) */
.plans-tile__cta-btn {
  display: block;
  width: 100%;
  padding: 12px 18px;
  background: white;
  color: var(--bzioup-color-Z);
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 999px;
  text-align: center;
  font-weight: 600;
  font-size: 14px;
  font-family: inherit;
  cursor: pointer;
  transition: opacity 150ms ease, transform 150ms ease,
              border-color 150ms ease;
  box-sizing: border-box;
}

/* Popular tier (Super): pink pill. Matches the tile's pink theme
   so the eye lands on this CTA first across the three tiles. */
.plans-tile--popular .plans-tile__cta-btn {
  background: var(--bzioup-color-pink);
  color: white;
  border-color: var(--bzioup-color-pink);
}

.plans-tile__cta-btn:hover {
  opacity: 0.92;
  transform: translateY(-1px);
}

.plans-tile__cta-btn:focus-visible {
  outline: 2px solid var(--bzioup-color-pink);
  outline-offset: 2px;
}

/* Sub-price aside, anchored at the bottom of the tile under the
   CTA. Italic + low opacity so it reads as a quiet brand-voice
   line ("You deserve it.", "After all, why not.", "Just in case.")
   rather than another piece of price information. */
.plans-tile__sub-price {
  margin: 0;
  font-size: 12px;
  font-style: italic;
  color: var(--bzioup-color-Y);
  opacity: 0.7;
}

@media (max-width: $mq-medium) {
  .plans-tiles__row {
    flex-direction: column;
    align-items: stretch;
  }
  .plans-tile {
    max-width: none;
  }
  .plans-tile--popular:hover {
    transform: translateY(-4px);
  }
}
</style>
